import styled from "@emotion/styled";
import {
  Message,
  MessageActionsProps,
  MessageSourceType,
} from "@lg-chat/message";
import { ToolUIPart, UIDataTypes, UIMessagePart, UITools } from "ai";
import { ContextChip } from "#Context";
import { ContextChips } from "#ContextChips";
import { ToolRenderer } from "./ToolRenderer";
import { getProgressByToolCallId } from "./ToolRenderer/utils";
import { FungiUIMessage } from "./types";

export const MessageRenderer: React.FC<
  FungiUIMessage &
    MessageActionsProps & {
      onChipClick?: (chip: ContextChip) => void;
      onLinkClick?: (href: string) => void;
    }
> = ({
  id,
  metadata,
  onChipClick,
  onClickCopy,
  onLinkClick,
  onRatingChange,
  onSubmitFeedback,
  parts,
  role,
}) => {
  const progressMap = getProgressByToolCallId(parts);

  return (
    <>
      {parts.map((part, index) => {
        const key = `${id}-${part.type}-${index}`;
        if (part.type === "text") {
          const isLastTextPart = !parts
            .slice(index + 1)
            .some((p) => p.type === "text");

          const isSender = role === "user";

          const displayText =
            isSender && metadata?.originalMessage
              ? metadata.originalMessage
              : part.text;
          const chips = metadata?.chips ?? [];

          return (
            <StyledMessage
              key={key}
              data-testid={`message-${role}`}
              isSender={isSender}
              messageBody={displayText}
              sourceType={MessageSourceType.Markdown}
            >
              {isSender && chips.length > 0 && (
                <ContextChips
                  chips={chips}
                  dismissible={false}
                  onClick={onChipClick}
                />
              )}
              {!isSender && part.state === "done" && isLastTextPart && (
                <Message.Actions
                  onClickCopy={onClickCopy}
                  onRatingChange={onRatingChange}
                  onSubmitFeedback={onSubmitFeedback}
                />
              )}
            </StyledMessage>
          );
        } else if (isToolUse(part)) {
          return (
            <ToolRenderer
              key={key}
              onLinkClick={onLinkClick}
              progress={progressMap.get(part.toolCallId)}
              {...part}
            />
          );
        }
        return null;
      })}
    </>
  );
};

const StyledMessage = styled(Message)`
  > div {
    max-width: 100%;
  }
`;

const isToolUse = (
  part: UIMessagePart<UIDataTypes, UITools>,
): part is ToolUIPart => part.type.startsWith("tool-");

export type { FungiUIMessage };
